import React, { useState, useEffect } from "react";
import {
  Box,
  Tabs,
  Tab,
  Typography,
  Button,
  LinearProgress,
  Checkbox,
  Avatar,
  IconButton,
  TextField,
  Paper,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from "@mui/material";
import { FaFileExcel, FaUpload, FaTrash } from "react-icons/fa";
import { toast } from "react-toastify";
import useApiEp from "../../hooks/useApiEp";

function ContactsTabs({
  campaignData,
  setCampaignData,
  isDragged,
  handleDragOver,
  handleDragLeave,
  handleDrop,
  handleFileUpload,
  downloadTemplate,
  uploadProgress,
  selectedCount,
  indeterminate,
  allSelected,
  handleSelectAll,
  searchQuery,
  setSearchQuery,
  filteredContacts,
  toggleSelect,
  removeContact,
}) {
  const [tabValue, setTabValue] = useState(0);
  const [selectedList, setSelectedList] = useState(
    campaignData.selectedList || ""
  );
  const [dbColumns, setDbColumns] = useState([]);
  const [quickListText, setQuickListText] = useState("");
  const [startIndex, setStartIndex] = useState(campaignData.startIndex || "");
  const [endIndex, setEndIndex] = useState(campaignData.endIndex || "");
  const {
    data: dbLists,
    isLoading: loadingList,
    isError,
    error,
  } = useApiEp("/user/get-my-assigned-dbs");

  // Modified API call to include startIndex and endIndex
  const {
    data: filterCount,
    isLoading: loadingFilterCount,
    isError: isErrorFilterCount,
    error: errorFilterCount,
  } = useApiEp(
    selectedList
      ? `/database/get-list-count?listId=${selectedList}${
          startIndex ? `&startIndex=${startIndex}` : ""
        }${endIndex ? `&endIndex=${endIndex}` : ""}`
      : "",
    !!selectedList // Enable the query only if selectedList is not empty
  );

  const checkForDuplicates = (newContacts) => {
    const existingPhones = new Set(campaignData.contacts.map((c) => c.phone));
    return newContacts.filter((contact) => {
      if (existingPhones.has(contact.phone)) {
        toast.warn(`Duplicate phone number ${contact.phone} skipped`);
        return false;
      }
      return true;
    });
  };

  const handleChangeList = (event) => {
    const listId = event.target.value;
    setSelectedList(listId);
    setCampaignData((prev) => ({ ...prev, selectedList: listId }));
    const list = dbLists?.find((l) => l._id === listId);
    setDbColumns(list ? list.columns : []);
  };

  const handleConfirmSelection = () => {
    if (!selectedList) {
      toast.error("Please select a list first");
      return;
    }

    setCampaignData((prev) => ({
      ...prev,
      contacts: [],
      selectedGroup: selectedList,
      startIndex: startIndex || undefined,
      endIndex: endIndex || undefined,
    }));

    toast.success(`Selected list with ID ${selectedList} for campaign`);
    setSelectedList("");
    setDbColumns([]);
    setStartIndex("");
    setEndIndex("");
  };

  const handleAddQuickList = () => {
    if (!quickListText.trim()) {
      alert("Please enter phone numbers");
      return;
    }

    const phoneNumbers = quickListText
      .split("\n")
      .map((line) => line.trim())
      .filter((line) => line.length > 0);
    if (phoneNumbers.length === 0) {
      alert("No valid phone numbers found");
      return;
    }

    const newContacts = checkForDuplicates(
      phoneNumbers.map((phone, index) => ({
        id: Date.now() + index,
        name: "",
        phone: phone,
        selected: true,
      }))
    );

    if (newContacts.length === 0) {
      toast.error("No new contacts added (all were duplicates)");
      return;
    }

    setCampaignData((prev) => ({
      ...prev,
      contacts: [...prev.contacts, ...newContacts],
      selectedGroup: "", // Clear selectGroup when adding quick list
    }));

    setQuickListText("");
    toast.success(`Added ${newContacts.length} contacts from quick list`);
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)}>
        <Tab label="Import from Excel" />
        <Tab label="Use Database" />
        <Tab label="Quick List" />
      </Tabs>

      {/* Import from Excel */}
      {tabValue === 0 && (
        <Box sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom color="textSecondary">
            Upload your contacts via Excel file
          </Typography>
          <Box
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={() => document.getElementById("file-upload").click()}
            sx={{
              border: "2px dashed #ccc",
              p: 4,
              textAlign: "center",
              cursor: "pointer",
              borderRadius: 2,
              bgcolor: isDragged ? "grey.100" : "background.paper",
            }}
          >
            <input
              id="file-upload"
              type="file"
              accept=".xlsx, .xls"
              style={{ display: "none" }}
              onChange={(e) => {
                const file = e.target.files[0];
                if (file) {
                  handleFileUpload(file, checkForDuplicates);
                }
              }}
            />
            <FaFileExcel
              size={48}
              color="#217346"
              style={{ marginBottom: 16 }}
            />
            <Typography variant="h6" gutterBottom>
              {campaignData.uploadedFile
                ? "File Uploaded!"
                : "Drop your Excel file here"}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Supports .xlsx and .xls files with phone numbers
            </Typography>
            <Button variant="contained" startIcon={<FaUpload />} sx={{ mt: 2 }}>
              Browse Files
            </Button>
            <Button
              variant="outlined"
              onClick={downloadTemplate}
              sx={{ mt: 2, ml: 2 }}
            >
              Download Template
            </Button>
          </Box>
          {uploadProgress > 0 && (
            <Box sx={{ mt: 3 }}>
              <LinearProgress variant="determinate" value={uploadProgress} />
              <Typography variant="body2" sx={{ mt: 1 }}>
                {uploadProgress === 100 ? `Uploaded` : `Uploading...`}{" "}
                {uploadProgress}%
              </Typography>
            </Box>
          )}
          {campaignData.contacts.length > 0 && (
            <Box sx={{ mt: 3 }}>
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
              >
                <Typography variant="h6" gutterBottom>
                  Uploaded Contacts ({selectedCount} selected /{" "}
                  {campaignData.contacts.length} total)
                </Typography>
                <Box display="flex" alignItems="center">
                  <Checkbox
                    indeterminate={indeterminate}
                    checked={allSelected}
                    onChange={handleSelectAll}
                  />
                  <Typography>Select All</Typography>
                </Box>
              </Box>
              <TextField
                fullWidth
                placeholder="Search by phone number"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                sx={{ mb: 2 }}
              />
              <Paper
                variant="outlined"
                sx={{ p: 2, maxHeight: 400, overflow: "auto" }}
              >
                {filteredContacts.map((contact) => (
                  <Box
                    key={contact.id}
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{ mb: 1, p: 1, bgcolor: "grey.50", borderRadius: 1 }}
                  >
                    <Box display="flex" alignItems="center" gap={2}>
                      <Checkbox
                        checked={contact.selected}
                        onChange={() => toggleSelect(contact.id)}
                      />
                      <Avatar
                        sx={{ width: 32, height: 32, bgcolor: "primary.main" }}
                      >
                        {contact.name ? contact.name.charAt(0) : "?"}
                      </Avatar>
                      <Box>
                        <Typography variant="body1">
                          {contact.name || "Unknown"}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          {contact.phone}
                        </Typography>
                      </Box>
                    </Box>
                    <IconButton
                      size="small"
                      onClick={() => removeContact(contact.id)}
                    >
                      <FaTrash color="#f44336" />
                    </IconButton>
                  </Box>
                ))}
              </Paper>
            </Box>
          )}
        </Box>
      )}

      {/* Use Database */}
      {tabValue === 1 && (
        <Box sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>
            Select a Saved List from Database
          </Typography>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel id="list-select-label">Contact List</InputLabel>
            <Select
              labelId="list-select-label"
              value={selectedList}
              label="Contact List"
              onChange={handleChangeList}
            >
              {dbLists?.map((list) => (
                <MenuItem key={list._id} value={list._id}>
                  {list.name} ({list.columns.length} columns)
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
            <TextField
              label="Start Index"
              type="number"
              size="small"
              value={startIndex}
              onChange={(e) => setStartIndex(e.target.value)}
              sx={{ flex: 1 }}
              inputProps={{ min: 0 }}
            />
            <TextField
              label="End Index"
              type="number"
              size="small"
              value={endIndex}
              onChange={(e) => setEndIndex(e.target.value)}
              sx={{ flex: 1 }}
              inputProps={{ min: startIndex || 0 }}
            />
          </Box>
          {loadingFilterCount && (
            <Typography variant="body2" color="textSecondary">
              Loading contact count...
            </Typography>
          )}
          {isErrorFilterCount && (
            <Typography variant="body2" color="error">
              Error fetching contact count: {errorFilterCount?.message}
            </Typography>
          )}
          {filterCount && (
            <Typography variant="subtitle1" gutterBottom>
              Total Contacts in List: {filterCount.count}
            </Typography>
          )}
          {dbColumns.length > 0 && (
            <>
              <Typography variant="subtitle1" gutterBottom>
                Column Preview:
              </Typography>
              <Paper
                variant="outlined"
                sx={{ p: 2, mb: 2, maxHeight: 300, overflow: "auto" }}
              >
                {dbColumns.map((column, idx) => (
                  <Box
                    key={idx}
                    sx={{ mb: 1, p: 1, bgcolor: "grey.50", borderRadius: 1 }}
                  >
                    <Typography variant="body1">{column}</Typography>
                  </Box>
                ))}
              </Paper>
              <Button
                variant="contained"
                fullWidth
                onClick={handleConfirmSelection}
              >
                Confirm Selection
              </Button>
            </>
          )}
          {campaignData.selectedGroup && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="h6" gutterBottom>
                Selected Group:{" "}
                {dbLists.find((l) => l._id === campaignData.selectedGroup)
                  ?.name || "Unknown"}
                {campaignData.startIndex && campaignData.endIndex
                  ? ` (Indices: ${campaignData.startIndex} - ${campaignData.endIndex})`
                  : ""}
              </Typography>
            </Box>
          )}
        </Box>
      )}

      {/* Quick List */}
      {tabValue === 2 && (
        <Box sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>
            Add Quick List (Phone Numbers only)
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Paste phone numbers below, one per line. Names will be empty for
            quick contacts.
          </Typography>
          <TextField
            multiline
            rows={8}
            fullWidth
            placeholder="eg. 9876543210&#10;9123456789&#10;9988776655"
            value={quickListText}
            onChange={(e) => setQuickListText(e.target.value)}
            sx={{ mb: 2 }}
          />
          <Button variant="contained" fullWidth onClick={handleAddQuickList}>
            Add Quick List
          </Button>
          {campaignData.contacts.length > 0 && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="h6" gutterBottom>
                All Contacts ({selectedCount} selected /{" "}
                {campaignData.contacts.length} total)
              </Typography>
              <Paper
                variant="outlined"
                sx={{ p: 2, maxHeight: 300, overflow: "auto" }}
              >
                {campaignData.contacts.map((contact) => (
                  <Box
                    key={contact.id}
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{ mb: 1, p: 1, bgcolor: "grey.50", borderRadius: 1 }}
                  >
                    <Box display="flex" alignItems="center" gap={2}>
                      <Avatar
                        sx={{ width: 32, height: 32, bgcolor: "primary.main" }}
                      >
                        {contact.name ? contact.name.charAt(0) : "?"}
                      </Avatar>
                      <Box>
                        <Typography variant="body1">
                          {contact.name || "Unknown"}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          {contact.phone}
                        </Typography>
                      </Box>
                    </Box>
                    <IconButton
                      size="small"
                      onClick={() => removeContact(contact.id)}
                    >
                      <FaTrash color="#f44336" />
                    </IconButton>
                  </Box>
                ))}
              </Paper>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
}

export default ContactsTabs;
